// src/router.tsx
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import AppShell from './shell/AppShell';
import { getBootState, type BootState } from './lib/bootCoordinator';
import RouteError from './components/RouteError';
import { log, now } from './lib/devLog';
import { setLastRoute } from './lib/profileStore';
import { verifyDirectRestaurantPayment } from './lib/merchantApi';
import { getAuth, signOut } from 'firebase/auth';
import {
  AboutPage,
  CaseStudiesPage,
  CollegesPage,
  CorporatesPage,
  ExploreCataloguePage,
  HowItWorksPage,
  LegacyQrResolver,
  ProcurementCategoryPage,
  ProcurementPage,
  ProductPage,
  ProfilePage,
  PublicContactPage,
  PublicEntry,
  PublicLayout,
  PublicPolicyPage,
  RequirementPage,
  RestaurantsPage,
  WebsiteSettingsPage,
} from './screens/public/PublicSite';
const RequirementsInbox = lazy(() => import('./screens/admin/Requirements'));

function lazyRoute<T extends React.ComponentType<any>>(loader: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await loader();
    } catch (error: any) {
      const message = String(error?.message || error || '').toLowerCase();
      const isStaleChunk =
        message.includes('failed to fetch dynamically imported module') ||
        message.includes('importing a module script failed') ||
        message.includes('error loading dynamically imported module');
      if (isStaleChunk && typeof window !== 'undefined') {
        const key = `bw.route-reload:${window.location.pathname}`;
        const alreadyRetried = sessionStorage.getItem(key);
        if (!alreadyRetried) {
          sessionStorage.setItem(key, String(Date.now()));
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.filter((name) => name.includes('bitewise-shell')).map((name) => caches.delete(name)));
          }
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw error;
    }
  });
}

const Welcome = lazyRoute(() => import('./screens/onboarding/Welcome'));
const Name = lazy(() => import('./screens/onboarding/Name'));
const Dob = lazy(() => import('./screens/onboarding/Dob'));
const AddressPick = lazy(() => import('./screens/onboarding/AddressPick'));
const AddressLabel = lazy(() => import('./screens/onboarding/AddressLabel'));
const Phone = lazy(() => import('./screens/onboarding/Phone'));
const Otp = lazy(() => import('./screens/onboarding/Otp'));
const PermLocation = lazy(() => import('./screens/onboarding/PermLocation'));
const PermNotifications = lazy(() => import('./screens/onboarding/PermNotifications'));
const PermMic = lazy(() => import('./screens/onboarding/PermMic'));
const Referral = lazy(() => import('./screens/onboarding/Referral'));
const SetPasskey = lazy(() => import('./screens/onboarding/SetPasskey'));
const Finish = lazy(() => import('./screens/onboarding/Finish'));

const Unlock = lazy(() => import('./screens/auth/Unlock'));
const QuickUnlock = lazy(() => import('./screens/auth/QuickUnlock'));

const Home = lazy(() => import('./screens/home/Home'));
const Compare = lazyRoute(() => import('./screens/compare/Compare'));
const Cart = lazy(() => import('./screens/cart/Cart'));
const Availability = lazy(() => import('./screens/availability/Availability'));
const RestaurantMenu = lazy(() => import('./screens/menu/RestaurantMenu'));

const Inbox = lazy(() => import('./screens/notifications/Inbox'));
const History = lazy(() => import('./screens/orders/History'));
const LiveTracking = lazy(() => import('./screens/orders/LiveTracking'));
const Settings = lazy(() => import('./screens/settings/Settings'));
const Feedback = lazy(() => import('./screens/feedback/Feedback'));
const AdminAnalytics = lazy(() => import('./screens/admin/Analytics'));
const AdminThemes = lazy(() => import('./screens/admin/Themes'));
const AdminDebug = lazy(() => import('./screens/admin/DebugAnalytics'));
const AdminLogin = lazy(() => import('./screens/admin/AdminLogin'));
const AdminResetPassword = lazy(() => import('./screens/admin/AdminResetPassword'));
const AdminMerchantOnboarding = lazy(() => import('./screens/admin/MerchantOnboardingReview'));
const AdminOperations = lazy(() => import('./screens/admin/Operations'));
const RestaurantStart = lazy(() => import('./screens/merchant/RestaurantStart'));
const RestaurantPortal = lazy(() => import('./screens/merchant/RestaurantPortal'));
const RestaurantsDirectory = lazy(() => import('./screens/merchant/RestaurantsDirectory'));
const MerchantOrdersDashboard = lazy(() => import('./screens/merchant/MerchantOrdersDashboard'));
const DirectCheckout = lazy(() => import('./screens/merchant/DirectCheckout'));
const NearbyOrder = lazy(() => import('./screens/merchant/NearbyOrder'));
const RasaWebOrder = lazyRoute(() => import('./screens/order/RasaWebOrder'));
const RasaWebOrderStatus = lazy(() => import('./screens/order/RasaWebOrderStatus'));
const RasaWebOrderHistory = lazy(() => import('./screens/order/RasaWebOrderHistory'));
const TableOrderFallback = lazy(() => import('./screens/order/TableOrderFallback'));
const GroupCartFallback = lazy(() => import('./screens/order/GroupCartFallback'));
const Offers = lazy(() => import('./screens/offers/Offers'));
const Terms = lazy(() => import('./screens/legal/Terms'));
const Privacy = lazy(() => import('./screens/legal/Privacy'));
const PartnerTerms = lazy(() => import('./screens/legal/PartnerTerms'));
const PartnerPrivacy = lazy(() => import('./screens/legal/PartnerPrivacy'));
const PartnerAccountDeletion = lazy(() => import('./screens/legal/PartnerAccountDeletion'));
const RewardsPolicy = lazy(() => import('./screens/legal/RewardsPolicy'));
const AccountDeletion = lazy(() => import('./screens/legal/AccountDeletion'));
const Grievance = lazy(() => import('./screens/legal/Grievance'));
const RefundCancellation = lazy(() => import('./screens/legal/RefundCancellation'));
const DeliveryFulfilment = lazy(() => import('./screens/legal/DeliveryFulfilment'));
const Contact = lazy(() => import('./screens/legal/Contact'));
const ComingSoon = lazy(() => import('./screens/ComingSoon'));

let bootCache: BootState | null = null;
let bootLoaderRenderSeq = 0;
const SUSPENSE_FALLBACK = (
  <main className="min-h-dvh bg-[#ffc200] text-slate-950">
    <section className="grid min-h-dvh place-items-center px-6">
      <div className="grid h-28 w-28 place-items-center rounded-[28px] border-[3px] border-slate-950/15 bg-[#ffc200] text-[42px] font-black tracking-[-0.08em] shadow-2xl shadow-slate-950/15">
        BW
      </div>
    </section>
  </main>
);

const withSuspense = (node: React.ReactElement) => (
  <Suspense fallback={SUSPENSE_FALLBACK}>{node}</Suspense>
);

/* ---------- Guards ---------- */
function useBootState(): BootState {
  const [state, setState] = useState<BootState>(() =>
    bootCache || {
      ready: false,
      authed: false,
      hasAccount: false,
    }
  );

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      getBootState().then((next) => {
        if (cancelled) return;
        bootCache = next;
        if (import.meta.env.DEV) {
          console.log('[boot] auth=', next.authed, 'hasAccount=', next.hasAccount);
        }
        setState(next);
      });
    };
    run();

    const onInvalidate = () => {
      bootCache = null;
      run();
    };
    window.addEventListener('bw:boot:invalidated', onInvalidate as any);
    return () => {
      cancelled = true;
      window.removeEventListener('bw:boot:invalidated', onInvalidate as any);
    };
  }, []);

  return state;
}

function CashfreeReturnRedirect() {
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const query = new URLSearchParams(window.location.search);
      const orderId = String(
        query.get('order_id') ||
          query.get('orderId') ||
          sessionStorage.getItem('bw.webqr.pendingCashfreeOrderId') ||
          '',
      ).trim();
      const nextTarget = orderId ? `/order/${encodeURIComponent(orderId)}` : '/rasa/orders';
      if (orderId) {
        try {
          await verifyDirectRestaurantPayment(orderId, {
            provider: 'cashfree',
            providerOrderId: orderId,
            status: 'paid',
          });
          sessionStorage.removeItem('bw.webqr.pendingCashfreeOrderId');
        } catch (err) {
          console.warn('[cashfree-return] payment verification deferred', err);
        }
      }
      if (!cancelled) setTarget(nextTarget);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  if (target) return <Navigate to={target} replace />;
  return (
    <main className="grid min-h-screen place-items-center bg-[#ffc200] px-6 text-center text-slate-950">
      <section className="rounded-[28px] bg-white p-6 shadow-xl">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-[#ffc200] text-xl font-black">
          BW
        </div>
        <h1 className="mt-4 text-2xl font-black">Confirming payment</h1>
        <p className="mt-2 text-sm font-semibold text-slate-600">Please wait while we confirm your QR order.</p>
      </section>
    </main>
  );
}

function BootLoader() {
  const loc = useLocation();
  const renderId = ++bootLoaderRenderSeq;
  log('[ui] BootLoader render', {
    renderId,
    pathname: loc.pathname,
    ts: now(),
  });
  return null;
}

function EntryGate() {
  const { ready, authed, hasAccount } = useBootState();
  if (!ready) return <BootLoader />;
  if (authed) return <Navigate to="/home" replace />;
  if (hasAccount) return <Navigate to="/unlock" replace />;
  return withSuspense(<Welcome />);
}

function RequireAuthedOutlet() {
  const { ready, authed, hasAccount } = useBootState();
  if (!ready) return <BootLoader />;
  if (authed) return <Outlet />;
  const justUnlockedAt = Number(localStorage.getItem('bw.justUnlockedAt') || 0);
  if (justUnlockedAt && Date.now() - justUnlockedAt < 10000) {
    if (import.meta.env.DEV) {
      console.debug('[guard] recent unlock; waiting for auth hydration', {
        authed,
        hasAccount,
      });
    }
    return <BootLoader />;
  }
  if (hasAccount) return <Navigate to="/unlock" replace />;
  if (import.meta.env.DEV) {
    console.debug('[guard] redirect to signup', { authed, hasAccount });
  }
  return <Navigate to="/onboarding/auth/phone?mode=signup" replace />;
}

function RequirePortalAuthedOutlet() {
  const { ready, authed } = useBootState();
  const loc = useLocation();
  if (!ready) return <BootLoader />;
  if (authed) return <Outlet />;
  const target = `${loc.pathname}${loc.search || ''}`;
  try {
    setLastRoute(target);
    sessionStorage.setItem('bw.lastRoute', target);
  } catch {}
  return <Navigate to="/onboarding/auth/phone?mode=login" replace />;
}

function RequireAdminAuthedOutlet() {
  const { ready, authed } = useBootState();
  const loc = useLocation();
  if (!ready) return <BootLoader />;
  if (authed) return <Outlet />;
  const target = `${loc.pathname}${loc.search || ''}`;
  try {
    setLastRoute(target);
    sessionStorage.setItem('bw.lastRoute', target);
  } catch {}
  return <Navigate to="/admin/login" replace />;
}

function RequireRestaurantAuthedOutlet() {
  const { ready, authed } = useBootState();
  const loc = useLocation();
  if (!ready) return <BootLoader />;
  if (authed) return <Outlet />;
  const target = `${loc.pathname}${loc.search || ''}`;
  try {
    setLastRoute(target);
    sessionStorage.setItem('bw.lastRoute', target);
  } catch {}
  return <Navigate to="/restaurant/login" replace />;
}

function RequireVisitorOutlet() {
  const { ready, authed, hasAccount } = useBootState();
  const loc = useLocation();
  const path = loc.pathname;
  const search = loc.search || '';
  const isExplicitLoginRoute =
    path === '/onboarding/auth/phone' && new URLSearchParams(search).get('mode') === 'login';

  if (!ready) return <BootLoader />;

  // If already logged in and hitting root/onboarding root, send to home
  if (authed && (path === '/' || path === '/onboarding')) {
    return <Navigate to="/home" replace />;
  }

  if (ready && !authed && hasAccount) {
    const isOnboarding = path.startsWith('/onboarding');
    if (isExplicitLoginRoute) {
      return <Outlet />;
    }
    if (path === '/' || path === '/onboarding' || isOnboarding) {
      const justUnlockedAt = Number(localStorage.getItem('bw.justUnlockedAt') || 0);
      if (justUnlockedAt && Date.now() - justUnlockedAt < 10000) {
        if (import.meta.env.DEV) {
          console.debug('[guard] recent unlock; holding on auth screens', {
            authed,
            hasAccount,
          });
        }
        return <BootLoader />;
      }
      if (import.meta.env.DEV) {
        console.debug('[guard] redirect to unlock', { authed, hasAccount, path, search });
      }
      return <Navigate to="/unlock" replace />;
    }
  }

  // Allow onboarding/auth screens even if logged in (to avoid skipping OTP)
  return <Outlet />;
}

function LegacyRestaurantLoginRedirect() {
  const location = useLocation();
  return <Navigate to={`/restaurant/start${location.search || ''}`} replace />;
}

/* ---------- Reset route helper ---------- */
function Reset() {
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        await signOut(getAuth()).catch(() => {});
      } catch {}
      try {
        localStorage.clear();
        sessionStorage.clear();
        try { window.dispatchEvent(new Event('bw:auth:changed')); } catch {}
        try { window.dispatchEvent(new Event('bw:boot:invalidated')); } catch {}
        try { window.dispatchEvent(new Event('bw:perm:changed')); } catch {}
      } catch {}
      if (!cancelled) {
        window.location.replace('/onboarding/auth/phone?mode=login');
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
}

/* -------------------------------- Router ------------------------------- */
const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <PublicEntry /> },
      { path: '/procurement', element: <ProcurementPage /> },
      { path: '/procurement/product/:productSlug', element: <ProductPage /> },
      { path: '/procurement/request-quotation', element: <RequirementPage /> },
      { path: '/procurement/:categorySlug', element: <ProcurementCategoryPage /> },
      { path: '/restaurants', element: <RestaurantsPage /> },
      { path: '/restaurant/:restaurantSlug', element: <ProfilePage kind="restaurant" /> },
      { path: '/colleges', element: <CollegesPage /> },
      { path: '/college/:campusSlug', element: <ProfilePage kind="college" /> },
      { path: '/corporates', element: <CorporatesPage /> },
      { path: '/corporate/:companySlug', element: <ProfilePage kind="corporate" /> },
      { path: '/explore-catalogue', element: <ExploreCataloguePage /> },
      { path: '/suppliers', element: <RequirementPage supplier /> },
      { path: '/suppliers/join', element: <RequirementPage supplier /> },
      { path: '/suppliers/submit-catalogue', element: <RequirementPage supplier /> },
      { path: '/requirements/new', element: <RequirementPage /> },
      { path: '/requirements/recurring', element: <RequirementPage /> },
      { path: '/requirements/confirmation/:reference', element: <RequirementPage /> },
      { path: '/about', element: <AboutPage /> },
      { path: '/how-it-works', element: <HowItWorksPage /> },
      { path: '/case-studies', element: <CaseStudiesPage /> },
      { path: '/contact', element: <PublicContactPage /> },
      { path: '/website-settings', element: <WebsiteSettingsPage /> },
      { path: '/qr', element: <LegacyQrResolver /> },
      { path: '/legal/terms', element: <PublicPolicyPage>{withSuspense(<Terms />)}</PublicPolicyPage> },
      { path: '/legal/privacy', element: <PublicPolicyPage>{withSuspense(<Privacy />)}</PublicPolicyPage> },
      { path: '/legal/refund-cancellation', element: <PublicPolicyPage>{withSuspense(<RefundCancellation />)}</PublicPolicyPage> },
      { path: '/legal/delivery-fulfilment', element: <PublicPolicyPage>{withSuspense(<DeliveryFulfilment />)}</PublicPolicyPage> },
      { path: '/legal/contact', element: <PublicPolicyPage>{withSuspense(<Contact />)}</PublicPolicyPage> },
      { path: '/legal/grievance', element: <PublicPolicyPage>{withSuspense(<Grievance />)}</PublicPolicyPage> },
    ],
  },
  {
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { path: '/explore', element: withSuspense(<RasaWebOrder />) },
      { path: '/legal/partner/terms', element: withSuspense(<PartnerTerms />) },
      { path: '/legal/partner/privacy', element: withSuspense(<PartnerPrivacy />) },
      { path: '/legal/partner/delete-account', element: withSuspense(<PartnerAccountDeletion />) },
      { path: '/legal/rewards', element: withSuspense(<RewardsPolicy />) },
      { path: '/legal/delete-account', element: withSuspense(<AccountDeletion />) },
      { path: '/coming-soon/:variant', element: withSuspense(<ComingSoon />) },
      { path: '/merchant/onboarding', element: <Navigate to="/restaurant/start?mode=new" replace /> },
      { path: '/merchant', element: <Navigate to="/restaurant/start" replace /> },
      { path: '/restaurant', element: <Navigate to="/restaurant/start" replace /> },
      { path: '/restaurant/start', element: withSuspense(<RestaurantStart />) },
      { path: '/restaurant/onboarding', element: <Navigate to="/restaurant/start?mode=new" replace /> },
      { path: '/merchant/restaurants', element: withSuspense(<RestaurantsDirectory />) },
      { path: '/order-nearby', element: withSuspense(<NearbyOrder />) },
      { path: '/order-nearby/:platform/:restaurantId/menu', element: withSuspense(<RestaurantMenu />) },
      { path: '/rasa', element: withSuspense(<RasaWebOrder />) },
      { path: '/rasoi', element: withSuspense(<RasaWebOrder />) },
      { path: '/gaur_cafe', element: withSuspense(<RasaWebOrder />) },
      { path: '/chandafood', element: withSuspense(<RasaWebOrder />) },
      { path: '/networthycafe', element: withSuspense(<RasaWebOrder />) },
      { path: '/saleems', element: withSuspense(<RasaWebOrder />) },
      { path: '/rasa/order/:id', element: withSuspense(<RasaWebOrderStatus />) },
      { path: '/order/:id', element: withSuspense(<RasaWebOrderStatus />) },
      { path: '/rasa/orders', element: withSuspense(<RasaWebOrderHistory />) },
      { path: '/payment/cashfree/return', element: <CashfreeReturnRedirect /> },
      { path: '/order/rasa', element: withSuspense(<RasaWebOrder />) },
      { path: '/order/table', element: withSuspense(<RasaWebOrder />) },
      { path: '/order/table/install', element: withSuspense(<TableOrderFallback />) },
      { path: '/group-cart/:id', element: withSuspense(<GroupCartFallback />) },
      { path: '/admin', element: <Navigate to="/admin/operations" replace /> },
      { path: '/admin/login', element: withSuspense(<AdminLogin />) },
      { path: '/admin/reset-password', element: withSuspense(<AdminResetPassword />) },
      { path: '/restaurant/login', element: <LegacyRestaurantLoginRedirect /> },

      // Handy reset route
      { path: '/reset', element: <Reset /> },

      // Onboarding (ALWAYS start at phone)
      {
        element: <RequireVisitorOutlet />,
        children: [
          { path: '/onboarding',                    element: <Navigate to="/onboarding/auth/phone" replace /> },
          { path: '/onboarding/name',               element: withSuspense(<Name />) },
          { path: '/onboarding/dob',                element: withSuspense(<Dob />) },
          { path: '/onboarding/referral',           element: withSuspense(<Referral />) },
          { path: '/onboarding/address/pick',       element: withSuspense(<AddressPick />) },
          { path: '/onboarding/address/label',      element: withSuspense(<AddressLabel />) },
          { path: '/onboarding/auth/phone',         element: withSuspense(<Phone />) },
          { path: '/onboarding/auth/otp',           element: withSuspense(<Otp />) },
          { path: '/onboarding/perm/location',      element: withSuspense(<PermLocation />) },
          { path: '/onboarding/perm/notifications', element: withSuspense(<PermNotifications />) },
          { path: '/onboarding/perm/mic',           element: withSuspense(<PermMic />) },
          { path: '/onboarding/setpasskey',         element: withSuspense(<SetPasskey />) },
          { path: '/onboarding/finish',             element: withSuspense(<Finish />) },
        ]
      },

      // Returning users
      { path: '/unlock', element: withSuspense(<Unlock />) },
      { path: '/quick-unlock', element: withSuspense(<QuickUnlock />) },
      { path: '/unlock/otp', element: withSuspense(<Phone />) },
      { path: '/unlock/otp/verify', element: withSuspense(<Otp />) },

      // 🔁 DEPRECATED LEGACY ROUTE
      // Instead of rendering PasskeyLogin (which fakes auth by just setting bw.session.phone),
      // send everyone to /unlock. This keeps the "one true unlock path".
      { path: '/auth/passkey', element: <Navigate to="/unlock" replace /> },

      // Restaurant portal section
      {
        element: <RequireRestaurantAuthedOutlet />,
        children: [
          { path: '/restaurant/portal', element: withSuspense(<RestaurantPortal />) },
          { path: '/merchant/orders', element: withSuspense(<MerchantOrdersDashboard />) },
        ],
      },

      // Admin section
      {
        element: <RequireAdminAuthedOutlet />,
        children: [
          { path: '/admin/merchant-onboarding', element: withSuspense(<AdminMerchantOnboarding />) },
          { path: '/admin/operations', element: withSuspense(<AdminOperations />) },
          { path: '/admin/requirements', element: withSuspense(<RequirementsInbox />) },
        ],
      },

      // Protected app section
      {
        element: <RequireAuthedOutlet />,
        children: [
          { path: '/home',           element: withSuspense(<Home />) },
          { path: '/compare/:id',    element: withSuspense(<Compare />) },
          { path: '/cart',           element: withSuspense(<Cart />) },
          { path: '/availability',   element: withSuspense(<Availability />) },
          { path: '/restaurant/:platform/:restaurantId/menu', element: withSuspense(<RestaurantMenu />) },
          { path: '/restaurant/direct/:restaurantId/checkout', element: withSuspense(<DirectCheckout />) },

          // Hamburger routes (real screens)
          { path: '/notifications',  element: withSuspense(<Inbox />) },
          { path: '/tasks',          element: <Navigate to="/home" replace /> },
          { path: '/leaderboard',    element: <Navigate to="/home" replace /> },
          { path: '/orders/history', element: withSuspense(<History />) },
          { path: '/orders/:id', element: withSuspense(<LiveTracking />) },
          { path: '/settings',       element: withSuspense(<Settings />) },
          { path: '/achievements',   element: <Navigate to="/home" replace /> },
          { path: '/feedback',       element: withSuspense(<Feedback />) },
          { path: '/admin/analytics', element: withSuspense(<AdminAnalytics />) },
          { path: '/admin/themes', element: withSuspense(<AdminThemes />) },
          { path: '/admin/debug', element: withSuspense(<AdminDebug />) },
          { path: '/offers',         element: withSuspense(<Offers />) },
        ],
      },

      // Fallbacks
      { path: '/compare', element: <Navigate to="/availability" replace /> },
      { path: '*',        element: <Navigate to="/" replace /> },  // safer default
    ],
  },
]);

export default router;
